import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Filter, BookOpen, Users } from 'lucide-react'
import { coursesApi, programsApi } from '../../api/client'

const LEVELS = ['beginner', 'intermediate', 'advanced']
const TAGS = ['Independent course', 'Diploma course', 'Python', 'Linux', 'CUDA', 'ROCm', 'InfiniBand', 'RDMA', 'RoCE', 'OFED', 'OpenMPI', 'DOCA', 'UCX']

export default function CourseCatalogPage() {
  const [search, setSearch] = useState('')
  const [level, setLevel] = useState('')
  const [tag, setTag] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['courses', { search, level, tag, page }],
    queryFn: () => coursesApi.list({ search: search || undefined, level: level || undefined, tag: tag || undefined, page }).then(r => r.data),
  })
  const programQuery = useQuery({
    queryKey: ['programs'],
    queryFn: () => programsApi.list().then(response => response.data),
  })

  return (
    <div className="max-w-[1180px] mx-auto px-6 py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold mb-2">Courses and Programmes</h1>
        <p className="text-brand-muted">Book-based independent courses and applied computer science for modern AI/HPC infrastructure.</p>
      </div>

      <section className="mb-8" aria-label="Diploma programmes">
        {programQuery.isLoading && <p className="text-brand-muted">Loading programmes...</p>}
        {programQuery.isError && <p role="alert">Unable to load programmes. <button className="btn-outline" onClick={() => programQuery.refetch()}>Retry</button></p>}
        {programQuery.data?.map(program => (
          <article key={program.slug} className="card">
            <span className="badge badge-amber">Coming soon</span>
            <h2 className="text-xl font-bold my-3"><Link to={`/programs/${program.slug}`}>{program.title}</Link></h2>
            <p className="text-brand-muted mb-4">{program.description}</p>
            <Link className="btn-outline" to={`/programs/${program.slug}`}>View the {program.courses.length}-course programme</Link>
          </article>
        ))}
      </section>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-8">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input className="input pl-9" placeholder="Search courses…" value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
        </div>
        <select className="input w-auto" value={level} onChange={e => { setLevel(e.target.value); setPage(1) }}>
          <option value="">All levels</option>
          {LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
        </select>
        <select className="input w-auto" value={tag} onChange={e => { setTag(e.target.value); setPage(1) }}>
          <option value="">All topics</option>
          {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(search || level || tag) && (
          <button className="btn-ghost text-sm" onClick={() => { setSearch(''); setLevel(''); setTag(''); setPage(1) }}>
            <Filter size={14}/> Clear
          </button>
        )}
      </div>

      {/* Grid */}
      {isError ? (
        <div role="alert">Unable to load courses. <button className="btn-outline" onClick={() => refetch()}>Retry</button></div>
      ) : isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => <CourseCardSkeleton key={i} />)}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="text-center py-20 text-brand-muted">
          <BookOpen size={40} className="mx-auto mb-4 opacity-30" />
          <p>No courses found. Check back soon.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data?.items?.map((course: any) => <CourseCard key={course.id} course={course} />)}
          </div>
          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-outline py-1.5 px-3 text-sm disabled:opacity-40">← Prev</button>
              <span className="flex items-center px-3 text-sm text-brand-muted">Page {page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="btn-outline py-1.5 px-3 text-sm disabled:opacity-40">Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function CourseCard({ course }: { course: any }) {
  const levelColors: Record<string, string> = { beginner: 'badge-green', intermediate: 'badge-blue', advanced: 'badge-amber' }
  return (
    <Link to={`/learn/${course.slug}`} className="card flex flex-col gap-3 no-underline group">
      {course.thumbnail_url ? (
        <img src={course.thumbnail_url} alt={course.title} className="w-full h-40 object-cover rounded-lg" />
      ) : (
        <div className="w-full h-40 rounded-lg bg-brand-surface2 flex items-center justify-center text-4xl font-black text-brand-muted">
          {course.title.charAt(0)}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`badge ${levelColors[course.level] || 'badge-blue'}`}>{course.level}</span>
        {!course.is_enrollment_open ? <span className="badge badge-amber">Coming soon</span> : course.is_free && <span className="badge badge-green">Free</span>}
        {course.tags?.slice(0, 2).map((t: string) => (
          <span key={t} className="badge badge-blue">{t}</span>
        ))}
      </div>
      <h3 className="font-bold text-base group-hover:text-brand-blue transition-colors">{course.title}</h3>
      {course.subtitle && <p className="text-brand-muted text-sm line-clamp-2">{course.subtitle}</p>}
      <div className="mt-auto flex items-center gap-4 text-xs text-brand-muted">
        <span className="flex items-center gap-1"><BookOpen size={12}/>{course.lesson_count} lessons</span>
        <span className="flex items-center gap-1"><Users size={12}/>{course.enrolled_count} enrolled</span>
      </div>
      <p className="text-xs text-brand-muted">Managed by {course.instructor?.username === 'admin' ? 'PacketFive Academy' : course.instructor?.full_name || course.instructor?.username}</p>
    </Link>
  )
}

function CourseCardSkeleton() {
  return (
    <div className="card animate-pulse">
      <div className="h-40 rounded-lg bg-brand-surface2 mb-3" />
      <div className="h-4 bg-brand-surface2 rounded w-3/4 mb-2" />
      <div className="h-3 bg-brand-surface2 rounded w-full mb-1" />
      <div className="h-3 bg-brand-surface2 rounded w-2/3" />
    </div>
  )
}
